// use-surgeons.ts
// This file provides hooks for fetching, searching, subscribing, and unsubscribing surgeons for the current user.
// Integrates with Athena/Cerner context and React Query for state management.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppState } from "@/contexts/app-state";
import { SurgeonSearch } from "@/types/surgeons";
import { getSubscribedSurgeons, searchSurgeons, postSubscribedSurgeon, deleteSubscribedSurgeon } from "@/services/surgeons";

/**
 * useGetSubscribedSurgeons Hook
 * Fetches the list of surgeons the current user is subscribed to.
 *
 * @returns The query object containing data, status, and helpers from React Query
 *
 * @example
 * const { data, isLoading } = useGetSubscribedSurgeons();
 */
export const useGetSubscribedSurgeons = () => {
  const { loggedInUser, context } = useAppState();

  return useQuery({
    queryKey: ["subscribed-surgeons", { user: loggedInUser }],
    queryFn: () =>
      getSubscribedSurgeons({
        user: loggedInUser,
        practiceId: context?.athena?.practiceId || "8363",
        requestorRole: context?.user || "local-dev",
      }),
  });
};

/**
 * useSearchSurgeons Hook
 * Provides a mutation for searching surgeons by name or NPI.
 *
 * @returns An object with mutation state, data, and a search function
 *
 * @example
 * const { search, data, isPending } = useSearchSurgeons();
 * search("Smith");
 */
export const useSearchSurgeons = () => {
  const { loggedInUser, context } = useAppState();
  const { data, isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: searchSurgeons,
  });

  const search = (searchInput: string) => {
    mutate({
      searchInput,
      user: loggedInUser,
      practiceId: context?.athena?.practiceId || "8363",
    });
  };

  return {
    data,
    isPending,
    isError,
    isSuccess,
    error,
    search,
  };
};

/**
 * useSubscribeToSurgeon Hook
 * Provides a mutation for subscribing the current user to a surgeon.
 *
 * @returns An object with mutation state, data, and a subscribe function
 *
 * @example
 * const { subscribe, isPending } = useSubscribeToSurgeon();
 * subscribe(surgeonObj);
 */
export const useSubscribeToSurgeon = () => {
  const queryClient = useQueryClient();
  const { loggedInUser, context } = useAppState();
  const { data, isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: postSubscribedSurgeon,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["subscribed-surgeons", { user: loggedInUser }],
      });
    },
  });

  const subscribe = (surgeon: SurgeonSearch, onSettled?: () => Promise<void>) => {
    mutate({
      ...surgeon,
      surgeon: surgeon.displayName,
      user: loggedInUser,
      practiceId: context?.athena?.practiceId || "8363",
    },
      {
        onSettled: () => {
          if (onSettled) onSettled().then(data => data).catch((e) => console.error(e));
        }
      }
    );
  };

  return {
    data,
    isPending,
    isError,
    isSuccess,
    error,
    subscribe,
  };
};

/**
 * useUnsubscribeFromSurgeon Hook
 * Provides a mutation for unsubscribing the current user from a surgeon.
 *
 * @returns An object with mutation state, data, and an unsubscribe function
 *
 * @example
 * const { unsubscribe, isPending } = useUnsubscribeFromSurgeon();
 * unsubscribe("1234567890");
 */
export const useUnsubscribeFromSurgeon = () => {
  const queryClient = useQueryClient();
  const { loggedInUser } = useAppState();
  const { data, isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: deleteSubscribedSurgeon,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["subscribed-surgeons", { user: loggedInUser }],
      });
    },
  });

  const unsubscribe = (npi: string, onSettled?: () => Promise<void>) => {
    mutate({
      user: loggedInUser,
      npi,
    }, {
      onSettled: () => {
        if (onSettled) onSettled().then(data => data).catch((e) => console.error(e));
      }
    });
  };

  return {
    data,
    isPending,
    isError,
    isSuccess,
    error,
    unsubscribe,
  };
};

/**
 * useSurgeons Hook
 * Combines all surgeon-related hooks into a single interface for convenience.
 *
 * @returns An object with get, search, post, and delete helpers
 *
 * @example
 * const { get, search, post, delete } = useSurgeons();
 */
export const useSurgeons = () => {
  return {
    get: useGetSubscribedSurgeons(),
    search: useSearchSurgeons(),
    post: useSubscribeToSurgeon(),
    delete: useUnsubscribeFromSurgeon(),
  };
};
